// src/api/envelope.ts
import { api } from "../api";

/** Envelope payload shape (keep loose if your backend varies) */
export type EnvelopeOpenResponse = {
  id?: string;
  
  title?: string;
  hasPdf?: boolean;
  contractURL?: string | null;
  bottomLayer?: any[];
  inputLayer?: any[];
  recipient?: {
    name?: string;
    email?: string;
    role?: string;
    signed?: boolean;
    signedAt?: string;
    /** The channel this link was sent to */
    via?: ChannelInfo | null;
    /** The channel that was used to sign (may be another link) */
    signedVia?: ChannelInfo | null;
    signedHere?: boolean;
    linkCount?: number;
  } | null;
  recipients?: { name?: string; email: string; role?: string }[];
  [key: string]: any;
};

export type ChannelInfo = { kind: "email" | "sms"; display: string };

export type RecipientContactStatus = {
  kind: "email" | "sms";
  value: string;
  display: string;
  openedAt?: string;
  lastSentAt?: string;
  deliveryError?: string;
};

export type OwnerRecipient = {
  email: string;
  name?: string;
  role: string;
  signed: boolean;
  signedAt?: string;
  openedAt?: string;
  signedVia?: { kind: "email" | "sms"; value: string; display: string } | null;
  contacts?: RecipientContactStatus[];
};

export type AuditEvent = {
  id?: string;
  displayRef?: string | null;

  type?: string;
  occurredAt?: string;

  // ✅ IMPORTANT: backend returns null sometimes
  signerEmail?: string | null;

  ip?: string | null;
  userAgent?: string | null;

  // optional extra info blob
  meta?: Record<string, any> | null;
};

export type EnvelopeAuditResponse = {
  events?: AuditEvent[];
};

/** OWNER view: /envelope/:token → backend /api/open-envelope-owner/:token */
export const fetchOwnerEnvelope = async (token: string): Promise<EnvelopeOpenResponse> => {
  const res = await api.get(`/api/open-envelope-owner/${token}`);
  return res.data;
};

/** RECIPIENT view: /sign/:token → backend /api/open-envelope-recipient/:token */
export const fetchRecipientEnvelope = async (token: string): Promise<EnvelopeOpenResponse> => {
  const res = await api.get(`/api/open-envelope-recipient/${token}`);
  return res.data;
};

/** OWNER audit: /envelope/:token → backend /api/envelope/:token/audit (adjust if your route differs) */
export const fetchEnvelopeAudit = async (token: string): Promise<EnvelopeAuditResponse> => {
  const res = await api.get(`/api/envelope/${token}/audit`);
  return res.data;
};

/* ===============================================================
   CONTRACT LIST (Document Status page)
   =============================================================== */

export type ContractListFilters = {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  sort?: string;
  dateFrom?: string;
  dateTo?: string;
};

export type ContractSummary = {
  contractName: string;
  contractURL: string;
  contractType: string;
  contractDescription: string;
  recipients: OwnerRecipient[];
  dateSent: string;
  dateCompleted: string | null;
  expiresAt: string | null;
  computedStatus: string;
  lastActivityAt: string | null;
};

export type PaginatedContractsResponse = {
  contracts: ContractSummary[];
  total: number;
  page: number;
  totalPages: number;
  statusCounts?: Record<string, number>;
};

export const fetchContractsPaginated = async (filters: ContractListFilters): Promise<PaginatedContractsResponse> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '') params.set(k, String(v));
  });
  const res = await api.get(`/api/fetch-user-contracts-paginated?${params.toString()}`);
  return res.data;
};

export const resendReminder = async (contractURL: string, recipientEmail?: string) => {
  const res = await api.post(`/api/envelope/${contractURL}/remind`, { recipientEmail });
  return res.data;
};

export const voidEnvelope = async (contractURL: string) => {
  const res = await api.post(`/api/envelope/${contractURL}/void`);
  return res.data;
};

export const deleteEnvelope = async (contractURL: string) => {
  const res = await api.delete(`/api/envelope/${contractURL}`);
  return res.data;
};
